<template>
  <v-container class="base-reviews-list">
    <v-row justify="center">
      <v-col cols="12" v-if="compLoaded">
        <v-row no-gutters v-if="facilitiesCount">
          <v-col cols="12">
            <v-row>
              <v-col>
                <Pagination
                  class="flex-grow-1"
                  :page="page"
                  :per-page="perPage"
                  :options="perPageOptions"
                  :total-count="totalCount"
                  @change="handlePageChange"
                >
                </Pagination>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pt-0">
                <div style="min-height:300px">
                  <template v-if="showReviewsList">
                    <TransitionGroup name="fade" tag="div">
                      <AutoReplyReviewItem
                        v-for="review in reviews.results"
                        :key="review.id"
                        :item="review"
                        @action-success="removeItem(review.id)"
                        @action-error="fetchReviews"
                      >
                        <template #tags>
                          <ReviewTagsSelector
                            v-if="canTagReviews"
                            :is-loading="isLoading"
                            :available-tags="review.tags"
                            :review="review"
                            read-only
                            selected-only
                          />
                        </template>
                      </AutoReplyReviewItem>
                    </TransitionGroup>
                  </template>
                  <no-data
                    v-else
                    :description="$t('message.reviews.noSuggestedAnswerByAI')"
                  />
                  <SectionLoader :status="loading" />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <Pagination
                  class="flex-grow-1"
                  :page="page"
                  :per-page="perPage"
                  :options="perPageOptions"
                  :total-count="totalCount"
                  :show-options="false"
                  @change="handlePageChange"
                ></Pagination>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <SectionLoader :status="!compLoaded" />
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from "vuex";

import AutoReplyReviewItem from "@/views/reviews/AutoReplyReviewItem";
import SectionLoader from "@/components/Loader/SectionLoader";
import Pagination from "@/components/List/Pagination";
import ReviewTagsSelector from "@/components/Reviews/ReviewTagsSelector";
import NoData from "@/components/ui/NoData.vue";

import serviceHandler from "@/helpers/handlers/service";
import tagsService from "@/services/tags";
import autoReplies from "@/services/autoReplies";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";

export default {
  name: "AutoReplyReviewsList",
  components: {
    AutoReplyReviewItem,
    SectionLoader,
    Pagination,
    ReviewTagsSelector,
    NoData
  },
  mixins: [permissionsInfo, gtmEngagementMixin, setCompanyIdFromQueryMixin],
  data() {
    return {
      reviews: null,
      compLoaded: false,
      isLoading: false,
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      facilitiesCount: 0,
      tags: []
    };
  },

  async created() {
    await this.fetchReviews();
    this.facilitiesCount = this.$store.state.company.company.facilities_count;
    this.compLoaded = true;
  },

  mounted() {
    this.fetchTags();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  computed: {
    showReviewsList() {
      return (
        this.reviews && this.reviews.results && this.reviews.results.length
      );
    },
    ...mapState({
      loading: state => state.reviews.loading,
      selectedLocale: state => state.settings.selectedLocale
    }),
    requestParams() {
      return {
        page: this.page,
        perPage: this.perPage
      };
    }
  },

  methods: {
    async fetchReviews() {
      this.compLoaded = false;
      this.reviews = await serviceHandler(
        autoReplies.getReviews(this.requestParams)
      );
      if (this.reviews) {
        this.totalCount = this.reviews.count;
      }
      this.compLoaded = true;
    },
    async handlePageChange(
      { page, perPage } = { page: this.page, perPage: this.perPage }
    ) {
      this.page = page;
      this.perPage = perPage;
      await this.fetchReviews();
    },
    async fetchTags() {
      if (!this.canTagReviews) {
        return;
      }
      this.isLoading = true;
      this.tags = await serviceHandler(tagsService.getTags());
      this.isLoading = false;
    },
    async removeItem(id) {
      if (this.reviews.results.length === 1) {
        this.fetchReviews();
        return;
      }
      this.reviews.results = this.reviews.results.filter(
        review => review.id !== id
      );
      this.totalCount -= 1;
    }
  }
};
</script>

<style lang="scss" scoped>
.base-reviews-list {
  min-height: 300px;
}

.actions-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
}
.no-data {
  text-align: center;
  padding-top: 40px;
  max-width: 500px;
  margin: auto;
}

.min-w-230 {
  min-width: 230px;
}

.select-all-container {
  padding-bottom: $spacing * 2.25;
  margin-top: $spacing;
  border-bottom: 1px solid $base-light-2;

  ::v-deep {
    .v-input--selection-controls {
      padding-top: 0;
      margin-top: 0;

      &__input {
        margin-right: $spacing * 1.5;
      }
    }

    .v-label {
      @include font-size(14px);
      white-space: nowrap;
    }
  }
}

.sort-type {
  width: 260px;
  display: flex;
  flex-direction: row;
  align-items: center;
}

::v-deep .sort-type-select {
  width: 100%;
  height: 100%;
  flex-shrink: 0;

  &.v-text-field--outlined.v-input--dense.v-text-field--outlined {
    > .v-input__control {
      > .v-input__slot {
        padding-left: $spacing;
        padding-right: 0;
        min-height: 46px;
        height: 46px;
        @include font-size(14px);
        line-height: 17px;
        font-weight: normal;
      }
    }

    fieldset {
      border-color: rgba($text, 0.08);
      border-width: 1px;
      border-radius: $container-border-radius;
    }
  }

  &.v-text-field--outlined.v-input--dense.v-input--is-focused fieldset {
    border-color: $primary;
  }

  &.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined
    .v-input__append-inner {
    width: 30px;
    margin-top: auto;
    margin-bottom: auto;
    margin-right: $spacing * 1.5;
    padding-left: $spacing;
  }

  .v-select__selection--comma {
    margin-left: $spacing * 1.5 !important;
  }

  .v-label {
    @include font-size(14px);
    line-height: 17px;
    font-weight: normal;
    color: $dark-blue-3;
    padding-left: $spacing * 1.5;
    margin: $spacing / 8 0;
  }
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
